import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { I18nProvider } from "@lingui/react";
import {
  i18n,
  activateLocale,
  getDefaultLocale,
  parseLocaleFromPathname,
} from "@/i18n.ts";
import type { Locale } from "@/i18n";

type LanguageContextValue = {
  locale: Locale;
  isLoading: boolean;
  setLocale: (locale: Locale) => Promise<void>;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

const LanguageProviderFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F5FAFF] text-[#166B93] font-semibold">
    Loading...
  </div>
);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const pathLocale = parseLocaleFromPathname(location.pathname);
  const resolvedLocale: Locale = pathLocale ?? getDefaultLocale();

  const [locale, setLocaleState] = useState<Locale | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const sync = async () => {
      setIsLoading(true);
      try {
        await activateLocale(resolvedLocale);
        if (isMounted) {
          setLocaleState(resolvedLocale);
        }
      } catch (error) {
        console.error("Failed to activate locale", error);
        if (isMounted) {
          try {
            await activateLocale("en");
            setLocaleState("en");
          } catch {
            setLocaleState("en");
          }
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void sync();

    return () => {
      isMounted = false;
    };
  }, [resolvedLocale]);

  const handleLocaleChange = useCallback(
    async (nextLocale: Locale) => {
      if (nextLocale === locale) {
        return;
      }
      const onLocalePath = parseLocaleFromPathname(location.pathname);
      if (onLocalePath) {
        try {
          await activateLocale(nextLocale);
        } catch (error) {
          console.error("Failed to switch locale", error);
          return;
        }
        const segments = location.pathname.split("/").filter(Boolean);
        segments[0] = nextLocale;
        const pathname = `/${segments.join("/")}`;
        navigate(
          { pathname, search: location.search, hash: location.hash },
          { replace: true },
        );
        setLocaleState(nextLocale);
        return;
      }
      setIsLoading(true);
      try {
        await activateLocale(nextLocale);
        setLocaleState(nextLocale);
      } catch (error) {
        console.error("Failed to switch locale", error);
      } finally {
        setIsLoading(false);
      }
    },
    [locale, location.hash, location.pathname, location.search, navigate],
  );

  const value = useMemo<LanguageContextValue | null>(() => {
    if (!locale) {
      return null;
    }
    return {
      locale,
      isLoading,
      setLocale: handleLocaleChange,
    };
  }, [handleLocaleChange, isLoading, locale]);

  if (!value) {
    return <LanguageProviderFallback />;
  }

  return (
    <LanguageContext.Provider value={value}>
      <I18nProvider i18n={i18n}>{children}</I18nProvider>
    </LanguageContext.Provider>
  );
};

/** @see https://github.com/ArnaudBarre/eslint-plugin-react-refresh#only-export-components */
// eslint-disable-next-line react-refresh/only-export-components -- hook paired with provider
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
